// Global Instructions Rule Applied!

import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useDrop } from 'react-dnd'
import { Tooltip } from 'antd'
import { portalColors } from '../../../../../portal-configs/colors.js'
import { KanbanColumnProps, KanbanItem } from '../../interfaces.ts'
import { getColumnColour } from '../../controller/parts/colorUtils.ts'
import { KanbanCard } from './KanbanCard.tsx'
import { DropZone } from './DropZone.tsx'
import { ITEM_TYPES_EXPORT } from './DropZone.tsx'
import { KANBAN_COLUMN_SCROLL_CLASS } from '../../controller/parts/autoScrollUtils.ts'

export function KanbanColumn<T extends KanbanItem>({
  column,
  items,
  cardConfig,
  columnHeaderConfig,
  onMoveItem,
  onUpdateItem,
  successfullyMovedCardId,
  sameColumnOnly = false
}: KanbanColumnProps<T>): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null)
  const [draggedCardHeight, setDraggedCardHeight] = useState<number | null>(null)

  const isReadOnlyColumn = column.dropOption === false

  // Use a global monitor to track dragged item height
  const [{ draggedItem }] = useDrop(
    () => ({
      accept: ITEM_TYPES_EXPORT.KANBAN_ITEM,
      collect: (monitor) => ({
        draggedItem: monitor.getItem()
      })
    }),
    []
  )

  // Update dragged card height when drag item changes
  useEffect(() => {
    if (draggedItem && typeof draggedItem === 'object' && 'cardHeight' in draggedItem) {
      const height = draggedItem.cardHeight as number | null
      setDraggedCardHeight(height)
    } else {
      setDraggedCardHeight(null)
    }
  }, [draggedItem])

  const handleCardHeightChange = useCallback((itemId: string, height: number): void => {
    // Card height is tracked via drag item
  }, [])

  const defaultColumnColor = getColumnColour(column)

  const handleMoveItem = useCallback(
    (itemId: string, fromColumnId: string, toColumnId: string, targetIndex: number): void => {
      onMoveItem(itemId, fromColumnId, toColumnId, targetIndex)
    },
    [onMoveItem]
  )

  const renderCount = useMemo(() => {
    if (columnHeaderConfig?.renderCount) {
      return columnHeaderConfig.renderCount(items.length)
    }
    return (
      <span className='text-xs font-medium' style={{ color: portalColors.neutral[600] }}>
        {items.length} items
      </span>
    )
  }, [items.length, columnHeaderConfig])

  const renderTotal = useMemo(() => {
    if (columnHeaderConfig?.renderTotal) {
      return columnHeaderConfig.renderTotal(items.length, column)
    }
    return null
  }, [items.length, column, columnHeaderConfig])

  const columnColor = columnHeaderConfig?.getColumnColor ? columnHeaderConfig.getColumnColor(column) : defaultColumnColor

  return (
    <div
      ref={ref}
      className='bg-gradient-to-b from-gray-50 to-white rounded-xl p-3 flex flex-col transition-all duration-300 ease-out border kanban-column border-gray-200 hover:shadow-md'
      style={{
        height: 'calc(100vh - 200px)',
        maxHeight: 'calc(100vh - 200px)'
      }}
    >
      {/* Column Header */}
      <div className='mb-2 px-1'>
        <div className='flex flex-col'>
          {/* Title row */}
          <div className='flex items-center justify-center gap-1 pb-1'>
            {/* Column dot */}
            <div className='w-3 h-3 rounded-full' style={{ backgroundColor: columnColor }}></div>
            {/* Column title */}
            <h3 className='text-sm font-bold text-center' style={{ color: portalColors.neutral[900] }}>
              {column.name}
              {isReadOnlyColumn && (
                <Tooltip title={column.readOnlyReason || undefined}>
                  <span className='text-xs text-gray-500 font-normal ml-1'>(Read-only)</span>
                </Tooltip>
              )}
            </h3>
          </div>

          {/* Count and total row */}
          <div className='flex items-center justify-between pb-1'>
            {/* Item count on left */}
            <div className='flex items-center gap-1'>{renderCount}</div>

            {/* Total on right */}
            {renderTotal && (
              <div className='text-xs' style={{ color: portalColors.neutral[900] }}>
                {renderTotal}
              </div>
            )}
          </div>

          {/* Header underline */}
          <div className='w-full h-0.5' style={{ backgroundColor: columnColor }}></div>
        </div>
      </div>

      {/* Cards Container */}
      <div
        className={`flex-1 overflow-y-auto overflow-x-hidden space-y-1 group transition-all duration-300 kanban-scrollbar ${KANBAN_COLUMN_SCROLL_CLASS}`}
      >
        {items.map((item, index) => (
          <React.Fragment key={cardConfig.getItemId(item)}>
            {/* Drop zone before the first item */}
            {index === 0 && (
              <DropZone
                column={column}
                targetIndex={0}
                onMoveItem={handleMoveItem}
                label={`Drop above ${cardConfig.getItemTitle(item)}`}
                draggedCardHeight={draggedCardHeight}
                sameColumnOnly={sameColumnOnly}
              />
            )}

            <KanbanCard
              item={item}
              index={index}
              column={column}
              cardConfig={cardConfig}
              onMoveItem={onMoveItem}
              onUpdateItem={onUpdateItem}
              onCardHeightChange={handleCardHeightChange}
              successfullyMovedCardId={successfullyMovedCardId}
            />

            {/* Drop zone after each item */}
            <DropZone
              column={column}
              targetIndex={index + 1}
              onMoveItem={handleMoveItem}
              label={`Drop after ${cardConfig.getItemTitle(item)}`}
              draggedCardHeight={draggedCardHeight}
              isLastDropZone={index === items.length - 1}
              sameColumnOnly={sameColumnOnly}
            />
          </React.Fragment>
        ))}

        {/* Empty state */}
        {items.length === 0 && (
          <DropZone
            column={column}
            targetIndex={0}
            onMoveItem={handleMoveItem}
            label={`Drop items in ${column.name}`}
            isEmpty={true}
            draggedCardHeight={draggedCardHeight}
            sameColumnOnly={sameColumnOnly}
          />
        )}
      </div>
    </div>
  )
}
